import React from 'react';
import { Plus, X } from 'lucide-react';

export interface Condition {
  field: string;
  operator: string;
  value: any;
}

interface FieldOption {
  value: string;
  label: string;
}

const CUSTOMER_FIELDS: FieldOption[] = [
  { value: 'orderCount', label: '訂單數量' },
  { value: 'totalSpent', label: '累計消費' },
  { value: 'daysSinceLastOrder', label: '距離上次下單天數' },
  { value: 'hasReferrer', label: '是否有推薦人' },
  { value: 'source', label: '來源' },
  { value: 'name', label: '姓名' },
  { value: 'phone', label: '電話' },
];

const REFERRER_FIELDS: FieldOption[] = [
  { value: 'directRefs', label: '直接推薦數' },
  { value: 'indirectRefs', label: '間接推薦數' },
  { value: 'networkSize', label: '網絡人數' },
  { value: 'totalOrderValue', label: '累計訂單額' },
  { value: 'totalCommission', label: '累計佣金' },
  { value: 'tier', label: '等級' },
  { value: 'name', label: '姓名' },
  { value: 'phone', label: '電話' },
];

const OPERATORS = [
  { value: 'eq', label: '=' },
  { value: 'ne', label: '≠' },
  { value: 'gt', label: '>' },
  { value: 'gte', label: '≥' },
  { value: 'lt', label: '<' },
  { value: 'lte', label: '≤' },
  { value: 'contains', label: '包含' },
  { value: 'in', label: '在列表' },
];

const BOOLEAN_FIELDS = new Set(['hasReferrer']);
const NUMERIC_FIELDS = new Set([
  'orderCount',
  'totalSpent',
  'daysSinceLastOrder',
  'directRefs',
  'indirectRefs',
  'networkSize',
  'totalOrderValue',
  'totalCommission',
]);

function getDefaultValue(field: string, operator: string): any {
  if (BOOLEAN_FIELDS.has(field)) return true;
  if (operator === 'in') return '';
  return '';
}

function normalizeValue(field: string, operator: string, raw: any): any {
  if (BOOLEAN_FIELDS.has(field)) {
    return raw === true || raw === 'true' || raw === 1 || raw === '1';
  }
  if (operator === 'in') {
    if (Array.isArray(raw)) return raw.join(',');
    return raw == null ? '' : String(raw);
  }
  if (NUMERIC_FIELDS.has(field) && raw !== '' && raw != null) {
    const n = Number(raw);
    if (!Number.isNaN(n)) return n;
  }
  return raw == null ? '' : raw;
}

interface ConditionBuilderProps {
  conditions: Condition[];
  onChange: (conditions: Condition[]) => void;
  targetType: 'customer' | 'referrer';
}

const ConditionBuilder: React.FC<ConditionBuilderProps> = ({ conditions, onChange, targetType }) => {
  const fields = targetType === 'customer' ? CUSTOMER_FIELDS : REFERRER_FIELDS;
  const rows = Array.isArray(conditions) ? conditions : [];

  const updateRow = (index: number, partial: Partial<Condition>) => {
    const next = rows.map((c, i) => (i === index ? { ...c, ...partial } : c));
    onChange(next);
  };

  const addRow = () => {
    const field = fields[0]?.value || 'phone';
    onChange([
      ...rows,
      { field, operator: 'eq', value: getDefaultValue(field, 'eq') },
    ]);
  };

  const removeRow = (index: number) => {
    onChange(rows.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-3">
      {rows.length === 0 && (
        <div className="text-sm text-gray-500 dark:text-gray-400">尚未添加條件（空條件代表永遠符合）</div>
      )}
      {rows.map((cond, idx) => {
        const isBool = BOOLEAN_FIELDS.has(cond.field);
        const isIn = cond.operator === 'in';
        return (
          <div key={idx} className="flex flex-col sm:flex-row gap-2 items-start">
            <select
              value={cond.field}
              onChange={(e) => {
                const newField = e.target.value;
                updateRow(idx, { field: newField, value: getDefaultValue(newField, cond.operator) });
              }}
              className="w-full sm:w-40 p-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-orange-500 outline-none"
            >
              {fields.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
            <select
              value={cond.operator}
              onChange={(e) => {
                const newOp = e.target.value;
                updateRow(idx, { operator: newOp, value: getDefaultValue(cond.field, newOp) });
              }}
              className="w-full sm:w-24 p-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-orange-500 outline-none"
            >
              {OPERATORS.map((op) => (
                <option key={op.value} value={op.value}>
                  {op.label}
                </option>
              ))}
            </select>
            {isBool ? (
              <select
                value={cond.value === true || cond.value === 'true' ? 'true' : 'false'}
                onChange={(e) => updateRow(idx, { value: e.target.value === 'true' })}
                className="w-full sm:flex-1 p-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-orange-500 outline-none"
              >
                <option value="true">是</option>
                <option value="false">否</option>
              </select>
            ) : isIn ? (
              <input
                type="text"
                value={Array.isArray(cond.value) ? cond.value.join(',') : String(cond.value ?? '')}
                onChange={(e) => updateRow(idx, { value: e.target.value })}
                placeholder="以逗號分隔多個值"
                className="w-full sm:flex-1 p-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-orange-500 outline-none"
              />
            ) : (
              <input
                type={NUMERIC_FIELDS.has(cond.field) ? 'number' : 'text'}
                value={cond.value ?? ''}
                onChange={(e) => updateRow(idx, { value: normalizeValue(cond.field, cond.operator, e.target.value) })}
                className="w-full sm:flex-1 p-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-orange-500 outline-none"
              />
            )}
            <button
              type="button"
              onClick={() => removeRow(idx)}
              className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
              title="移除條件"
            >
              <X size={18} />
            </button>
          </div>
        );
      })}
      <button
        type="button"
        onClick={addRow}
        className="inline-flex items-center gap-1 text-sm text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300 font-medium"
      >
        <Plus size={16} /> 添加條件
      </button>
    </div>
  );
};

export default ConditionBuilder;
